import { computed, signal } from '@angular/core';
import { environment } from '../../../environments/environment';

interface StoredTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  refreshExpiresAt: number;
  email: string;
}

const STORAGE_KEY = 'ppc.kc-tokens';

const _tokens = signal<StoredTokens | null>(loadFromStorage());

export const session = computed(() => {
  const t = _tokens();
  return t ? { email: t.email } : null;
});

export const isAuthenticated = computed(() => {
  const t = _tokens();
  return !!t && t.refreshExpiresAt > Date.now();
});

export function getBearer(): string | null {
  const t = _tokens();
  if (!t) return null;
  return t.accessToken;
}

export function getAccessExpiresAt(): number | null {
  return _tokens()?.expiresAt ?? null;
}

export function clearSession(): void {
  sessionStorage.removeItem(STORAGE_KEY);
  _tokens.set(null);
}

export async function loginWithPassword(email: string, password: string): Promise<void> {
  const params = new URLSearchParams();
  params.set('grant_type', 'password');
  params.set('client_id', environment.keycloak.clientId);
  params.set('username', email);
  params.set('password', password);

  const res = await fetch(tokenEndpoint(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params.toString(),
  });
  if (!res.ok) {
    throw new KeycloakLoginError(res.status, await safeText(res));
  }
  const body = await res.json();
  storeTokens(body);
}

export async function refresh(): Promise<boolean> {
  const t = _tokens();
  if (!t || t.refreshExpiresAt <= Date.now()) return false;

  const params = new URLSearchParams();
  params.set('grant_type', 'refresh_token');
  params.set('client_id', environment.keycloak.clientId);
  params.set('refresh_token', t.refreshToken);

  try {
    const res = await fetch(tokenEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    });
    if (!res.ok) {
      clearSession();
      return false;
    }
    const body = await res.json();
    storeTokens(body);
    return true;
  } catch {
    clearSession();
    return false;
  }
}

export async function logoutOnKeycloak(): Promise<void> {
  const t = _tokens();
  if (!t) return;
  const params = new URLSearchParams();
  params.set('client_id', environment.keycloak.clientId);
  params.set('refresh_token', t.refreshToken);
  try {
    await fetch(`${realmBase()}/protocol/openid-connect/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    });
  } catch {
  } finally {
    clearSession();
  }
}

export class KeycloakLoginError extends Error {
  /** OAuth `error` code from the response body, e.g. 'invalid_grant' (null if unparseable). */
  readonly oauthError: string | null;
  /** OAuth `error_description` from the response body, e.g. 'Invalid user credentials'. */
  readonly oauthErrorDescription: string | null;

  constructor(public readonly status: number, public readonly bodyText: string) {
    super(`Keycloak /token failed: ${status}`);
    let error: string | null = null;
    let description: string | null = null;
    try {
      const json = JSON.parse(bodyText) as { error?: string; error_description?: string };
      error = json.error ?? null;
      description = json.error_description ?? null;
    } catch {
      // non-JSON body — leave both null
    }
    this.oauthError = error;
    this.oauthErrorDescription = description;
  }
}

function storeTokens(tokenResponse: {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
}): void {
  const now = Date.now();
  const stored: StoredTokens = {
    accessToken: tokenResponse.access_token,
    refreshToken: tokenResponse.refresh_token,
    expiresAt: now + tokenResponse.expires_in * 1000,
    refreshExpiresAt: now + tokenResponse.refresh_expires_in * 1000,
    email: decodeEmailFromJwt(tokenResponse.access_token),
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  _tokens.set(stored);
}

function decodeEmailFromJwt(jwt: string): string {
  try {
    const payload = jwt.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { email?: string; preferred_username?: string };
    return claims.email ?? claims.preferred_username ?? '';
  } catch {
    return '';
  }
}

function loadFromStorage(): StoredTokens | null {
  try {
    const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredTokens;
    if (!parsed?.accessToken || !parsed.refreshExpiresAt || parsed.refreshExpiresAt <= Date.now()) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function tokenEndpoint(): string {
  return `${realmBase()}/protocol/openid-connect/token`;
}

function realmBase(): string {
  const base = environment.keycloak.url.replace(/\/$/, '');
  return `${base}/realms/${environment.keycloak.realm}`;
}

async function safeText(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return '';
  }
}
